import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import { createAppThunk, RootState } from '@mse-reg/app/store';
import {
  AcademicYearDto,
  CopyAcademicYearOptionsDto,
  CopySemesterOptionsDto,
  ISemesterManagementClient,
  PhaseDto,
  SemesterInfoDto,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { getSemesterFilterAsync } from '../selections/semester-selection.slice';

// Async Thunks
const refreshFiltersAsync = createAppThunk('semesterManagement/REFRESH_FILTERS', async (_: void, _deps, dispatch) => {
  dispatch(semesterManagementActions.setLoading(true));
  try {
    await dispatch(getSemesterFilterAsync() as any);
  } catch (err) {
    dispatch(addExceptionNotification(err, `Error while refreshing the semester filter!`));
  } finally {
    dispatch(semesterManagementActions.setLoading(false));
  }
});

const copyAcademicYearAsync = createAppThunk(
  'semesterManagement/COPY_ACADEMIC_YEAR',
  async (
    options: CopyAcademicYearOptionsDto,
    deps: { semesterManagementClient: ISemesterManagementClient },
    dispatch,
    getState,
  ) => {
    dispatch(semesterManagementActions.setLoading(true));
    try {
      const result = await deps.semesterManagementClient.copyAcademicYear(options);
      await dispatch(refreshFiltersAsync());
      const academicYears = (getState() as RootState).semesterSelection.semesterFilter.academicYears;
      const newAcademicYear = academicYears.find((a) => a.id === result);
      const oldAcademicYear = academicYears.find((a) => a.id === options.copyFromId);
      dispatch(semesterManagementActions.selectAcademicYear(newAcademicYear));
      dispatch(addSuccess(`Academic year was successfully copied from ${oldAcademicYear.name} to ${options.name}.`));
      return result;
    } catch (err) {
      const academicYears = (getState() as RootState).semesterSelection.semesterFilter.academicYears;
      const oldAcademicYear = academicYears.find((a) => a.id === options.copyFromId);
      dispatch(
        addExceptionNotification(err, `Error while copying academic year ${oldAcademicYear.name} to ${options.name}!`),
      );
    } finally {
      dispatch(semesterManagementActions.setLoading(false));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const copySemesterAsync = createAppThunk(
  'semesterManagement/COPY_SEMESTER',
  async (
    options: CopySemesterOptionsDto,
    deps: { semesterManagementClient: ISemesterManagementClient },
    dispatch,
    getState,
  ) => {
    dispatch(semesterManagementActions.setLoading(true));
    const state = getState() as RootState;
    const oldSemester = state.semesterSelection.semesterFilter.semesters.find((s) => s.id === options.copyFromId);
    const academicYears = state.semesterSelection.semesterFilter.academicYears;
    const academicYear = academicYears.find((a) => a.id === options.toAcademicYearId);
    try {
      const result = await deps.semesterManagementClient.copySemester(options);
      await dispatch(refreshFiltersAsync());
      dispatch(semesterManagementActions.setLoading(false));
      const semester = (getState() as RootState).semesterSelection.semesterFilter.semesters.find(
        (s) => s.id === result,
      );
      dispatch(
        addSuccess(
          `Semester ${oldSemester.name} was successfully copied to academic year ${academicYear.name} with the new name ${semester.name}.`,
        ),
      );
      return result;
    } catch (err) {
      dispatch(
        addExceptionNotification(err, `Error while copying semester ${oldSemester.name} to ${academicYear.name}!`),
      );
    } finally {
      dispatch(semesterManagementActions.setLoading(false));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const saveAcademicYearAsync = createAppThunk(
  'semesterManagement/SAVE_ACADEMIC_YEAR',
  async (academicYear: AcademicYearDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setLoading(true));
    try {
      let selectedAcademicYear = academicYear;
      if (academicYear.id) {
        await deps.semesterManagementClient.updateAcademicYear(academicYear);
      } else {
        selectedAcademicYear = await deps.semesterManagementClient.addAcademicYear(academicYear);
      }
      await dispatch(refreshFiltersAsync());

      dispatch(semesterManagementActions.selectAcademicYear(selectedAcademicYear));
      dispatch(addSuccess(`Academic year ${selectedAcademicYear.name} was successfully saved.`));
      return selectedAcademicYear.id;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while saving academic year ${academicYear.name}!`));
    } finally {
      dispatch(semesterManagementActions.setLoading(false));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const deleteAcademicYearAsync = createAppThunk(
  'semesterManagement/DELETE_ACADEMIC_YEAR',
  async (academicYear: AcademicYearDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setLoading(true));
    try {
      await deps.semesterManagementClient.deleteAcademicYear(academicYear.id);
      await dispatch(refreshFiltersAsync());
      dispatch(semesterManagementActions.selectAcademicYear(null));
      dispatch(addSuccess(`Academic year ${academicYear.name} was successfully deleted.`));
      return academicYear;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting academic year ${academicYear.name}!`));
    } finally {
      dispatch(semesterManagementActions.setLoading(false));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const updateSemesterAsync = createAppThunk(
  'semesterManagement/UPDATE_SEMESTER',
  async (semester: SemesterInfoDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setSemesterLoading(semester.id));
    try {
      await deps.semesterManagementClient.updateOrInsertSemester(semester);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Semester ${semester.name} was successfully saved.`));
      return semester;
    } catch (err) {
      dispatch(semesterManagementActions.setSemesterLoading(-1));
      dispatch(addExceptionNotification(err, `Error while saving semester ${semester.name}!`));
    } finally {
      dispatch(semesterManagementActions.setSemesterLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const deleteSemesterAsync = createAppThunk(
  'semesterManagement/DELETE_SEMESTER',
  async (semester: SemesterInfoDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setSemesterLoading(semester.id));
    try {
      await deps.semesterManagementClient.deleteSemester(semester?.id);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Semester ${semester.name} was successfully deleted.`));
      return semester;
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting semester ${semester.name}!`));
    } finally {
      dispatch(semesterManagementActions.setSemesterLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const closePhaseAsync = createAppThunk(
  'semesterManagement/CLOSE_PHASE',
  async (phase: PhaseDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setPhaseLoading(phase.id));
    try {
      await deps.semesterManagementClient.closePhase(phase.id);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Phase ${phase.name} was successfully closed.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while closing phase ${phase.name}!`));
    } finally {
      dispatch(semesterManagementActions.setPhaseLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const updatePhaseAsync = createAppThunk(
  'semesterManagement/UPDATE_PHASE',
  async (phase: PhaseDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setPhaseLoading(phase.id));
    try {
      const returnedId = await deps.semesterManagementClient.updateOrInsertPhase(phase);
      await dispatch(refreshFiltersAsync());
      if (!phase.id) {
        dispatch(addSuccess(`Phase ${phase.name} was successfully added.`));
      } else {
        dispatch(addSuccess(`Phase ${phase.name} was successfully saved.`));
      }
      return { ...phase, id: returnedId };
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while saving phase ${phase.name}!`));
    } finally {
      dispatch(semesterManagementActions.setPhaseLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const reopenPhaseAsync = createAppThunk(
  'semesterManagement/REOPEN_PHASE',
  async (phase: PhaseDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setPhaseLoading(phase.id));
    try {
      await deps.semesterManagementClient.reopenPhase(phase.id);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Phase ${phase.name} was successfully reopened.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while reopening phase ${phase.name}!`));
    } finally {
      dispatch(semesterManagementActions.setPhaseLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

const deletePhaseAsync = createAppThunk(
  'semesterManagement/DELETE_PHASE',
  async (phase: PhaseDto, deps: { semesterManagementClient: ISemesterManagementClient }, dispatch) => {
    dispatch(semesterManagementActions.setPhaseLoading(phase.id));
    try {
      await deps.semesterManagementClient.deletePhase(phase.id);
      await dispatch(refreshFiltersAsync());
      dispatch(addSuccess(`Phase ${phase.name} was successfully deleted.`));
    } catch (err) {
      dispatch(addExceptionNotification(err, `Error while deleting phase ${phase.name}!`));
    } finally {
      dispatch(semesterManagementActions.setPhaseLoading(-1));
    }
  },
  (r) => ({ semesterManagementClient: r.resolve<ISemesterManagementClient>('ISemesterManagementClient') }),
);

// State
const initialState = {
  loading: false,
  errorMessage: null as string | null,
  selectedAcademicYear: null as AcademicYearDto | null,
  newSemester: null as SemesterInfoDto | null,
  semesterLoading: -1,
  phaseLoading: -1,
};

export type SemesterManagementState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'semesterManagement',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setSemesterLoading(state, action: PayloadAction<number>) {
      state.semesterLoading = action.payload;
      state.newSemester = null;
    },
    setPhaseLoading(state, action: PayloadAction<number>) {
      state.phaseLoading = action.payload;
    },
    selectAcademicYear(state, action: PayloadAction<AcademicYearDto | null | undefined>) {
      state.selectedAcademicYear = action.payload ?? null;
      state.newSemester = null;
    },
    addAcademicYear(state, action: PayloadAction<AcademicYearDto>) {
      state.selectedAcademicYear = action.payload;
    },
    addSemester(state, action: PayloadAction<SemesterInfoDto | null>) {
      state.newSemester = action.payload;
    },
  },
});

export const semesterManagementReducer = slice.reducer;
export const semesterManagementActions = slice.actions;
export const semesterManagementThunks = {
  refreshFiltersAsync,
  copyAcademicYearAsync,
  copySemesterAsync,
  saveAcademicYearAsync,
  deleteAcademicYearAsync,
  updateSemesterAsync,
  deleteSemesterAsync,
  updatePhaseAsync,
  closePhaseAsync,
  reopenPhaseAsync,
  deletePhaseAsync,
};

// Legacy module compatibility
const initActions = () => {
  return Object.freeze({
    cancelAddingSemester: () => slice.actions.addSemester(null),
    addSemester(academicYear: AcademicYearDto, name?: string) {
      const start = DateTime.local().set({ year: academicYear.startYear });
      const end = start.plus({ weeks: 14 });
      const semester: SemesterInfoDto = {
        id: 0,
        name: name ?? '',
        nameLong: '',
        academicYearId: academicYear.id,
        start,
        end,
        earliestRegistrationDate: start.startOf('year'),
        phases: [],
        registrationOpen: true,
        isPast: end < DateTime.local(),
        hasRelatedData: false,
      } as unknown as SemesterInfoDto;
      return slice.actions.addSemester(semester);
    },
    addAcademicYear: () => addAcademicYearThunk(),
    selectAcademicYear: (academicYear?: AcademicYearDto | null) => selectAcademicYearThunk(academicYear),
    saveAcademicYearAsync: (academicYear: AcademicYearDto) => saveAcademicYearAsync(academicYear),
    copyAcademicYearAsync: (options: CopyAcademicYearOptionsDto) => copyAcademicYearAsync(options),
    copySemesterAsync: (options: CopySemesterOptionsDto) => copySemesterAsync(options),
    deleteAcademicYearAsync: (academicYear: AcademicYearDto) => deleteAcademicYearAsync(academicYear),
    updateSemesterAsync: (semester: SemesterInfoDto) => updateSemesterAsync(semester),
    deleteSemesterAsync: (semester: SemesterInfoDto) => deleteSemesterAsync(semester),
    updatePhaseAsync: (phase: PhaseDto) => updatePhaseAsync(phase),
    deletePhaseAsync: (phase: PhaseDto) => deletePhaseAsync(phase),
    closePhaseAsync: (phase: PhaseDto) => closePhaseAsync(phase),
    reopenPhaseAsync: (phase: PhaseDto) => reopenPhaseAsync(phase),
  });
};

// Thunks that need access to getState for sync operations
const addAcademicYearThunk = createAppThunk(
  'semesterManagement/ADD_ACADEMIC_YEAR_THUNK',
  async (_: void, _deps, dispatch, getState) => {
    const newestYear = getState()
      .semesterSelection.semesterFilter?.academicYears?.slice()
      .sort((s1, s2) => (s1.startYear < s2.startYear ? 1 : -1))[0];
    const startYear = newestYear.startYear + 1;
    const academicYear: AcademicYearDto = {
      id: 0,
      name: `AY${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`,
      startYear,
      hasSemesters: false,
    };
    dispatch(slice.actions.addAcademicYear(academicYear));
  },
);

const selectAcademicYearThunk = createAppThunk(
  'semesterManagement/SELECT_ACADEMIC_YEAR_THUNK',
  async (academicYear: AcademicYearDto | null | undefined, _deps, dispatch, getState) => {
    const selected = academicYear ?? (getState() as RootState).semesterSelection.selectedAcademicYear;
    dispatch(slice.actions.selectAcademicYear(selected));
  },
);

export type SemesterManagementActions = ReturnType<typeof initActions>;

export const semesterManagementModule = {
  initActions: () => initActions(),
  reducer: slice.reducer,
};
